import { downloadAuthenticatedFile } from 'shared/helpers/downloadAuthenticatedFile';

import request from '../request';

export type ImportJobStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'paused'
  | 'awaiting_approval'
  | 'approving'
  | 'approved'
  | 'rejected';

export type ImportJobMode = 'full' | 'validation_only';
export type ImportScope = 'delta' | 'full';

export interface ImportJob {
  id: string | null;
  jobId: string;
  status: ImportJobStatus;
  jobMode: ImportJobMode;
  importScope: ImportScope;
  supplierId: string | null;
  fileName: string;
  fileHash: string;
  fileS3Key: string;
  jobStarted: string | null;
  jobFinished: string | null;
  recordCount: number | null;
  emptyRows: number | null;
  filledRows: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ImportJobListResponse {
  importJobs: ImportJob[];
  total: number;
}

export interface ImportJobDetailsResponse {
  importJob: ImportJob;
}

export type FergusonAdhocSkuStatus =
  | 'already_imported'
  | 'in_active_import'
  | 'found'
  | 'access_denied'
  | 'not_found'
  | 'api_error';

export interface FergusonAdhocSkuResult {
  sku: string;
  status: FergusonAdhocSkuStatus;
  message: string | null;
}

export interface FergusonAdhocImportSummary {
  total: number;
  found: number;
  alreadyImported: number;
  inActiveImport: number;
  accessDenied: number;
  notFound: number;
  apiError: number;
}

export interface FergusonAdhocImportRequest {
  skus: string[];
  dryRun?: boolean;
}

export interface FergusonAdhocImportResponse {
  results: FergusonAdhocSkuResult[];
  importJob: ImportJob | null;
  summary: FergusonAdhocImportSummary;
}

export type ImportJobStageStatus =
  'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export type ImportJobStageName =
  | 'file-process'
  | 'row-validation'
  | 'category-mapping'
  | 'categorization'
  | 'uom-mapping'
  | 'vector-process'
  | 'exact-match'
  | 'semantic-match'
  | 'awaiting-approval'
  | 'apply-approval'
  | 'export-generate';

export interface ImportJobStage {
  id: string | null;
  jobId: string;
  stageName: ImportJobStageName;
  status: ImportJobStageStatus;
  startedAt: string | null;
  completedAt: string | null;
  progressCurrent: number;
  progressTotal: number;
  errorCount: number;
  attemptsMade: number;
  maxRetries: number;
  metrics: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
}

export interface ImportJobStagesResponse {
  stages: ImportJobStage[];
}

export interface ErrorCounts {
  errorCount: number;
  warningCount: number;
}

export interface ImportJobErrorCountsResponse {
  errors: ErrorCounts;
}

export interface RowValidationSummaryResponse {
  valid: number;
  invalid: number;
  warning: number;
}

export interface JobRowStatusItem {
  rowNumber: number;
  processingStatus: string;
  validationStatus: string;
}

export interface CategoryMappingSummaryResponse {
  totalValidRows: number;
  rowsNeedingMapping: JobRowStatusItem[];
}

export interface UomMappingSummaryResponse {
  totalValidRows: number;
  mapped: number;
  unmapped: number;
  rowsNeedingMapping: JobRowStatusItem[];
}

export interface ExactMatchSummaryResponse {
  totalValidRows: number;
  exact: number;
  invalid: number;
  unmatched: number;
}

export interface SemanticMatchSummaryResponse {
  totalValidRows: number;
  autoApproved: number;
  pendingReview: number;
  unmatched: number;
}

export interface CategorizationStatisticsResponse {
  totalProducts: number;
  categorizedProducts: number;
  uncategorizedProducts: number;
  confidenceDistribution: {
    high: number;
    medium: number;
    low: number;
    veryLow: number;
  };
  methodDistribution: {
    aiSuggestion: number;
    manualOverride: number;
    supplierProvided: number;
  };
}

export interface CategorizationProductItem {
  rowNumber: number;
  id: string;
  name: string;
  description: string | null;
  supplierCategory: string | null;
  supplierSubcategory: string | null;
  supplierSku: string;
  reopsCategoryId: string | null;
  reopsSubcategoryId: string | null;
  categorizationConfidence: number | null;
  categorizationMethod: string | null;
  reopsCategoryName: string | null;
  reopsSubcategoryName: string | null;
}

export interface CategorizationProductsResponse {
  products: CategorizationProductItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type ImportJobRowErrorSeverity = 'error' | 'warning';

export interface ImportJobRowError {
  id: string;
  jobId: string;
  rowNumber: number;
  errorCode: string;
  errorMessage: string;
  fieldPath: string | null;
  severity: ImportJobRowErrorSeverity;
  createdAt: string;
  updatedAt: string;
}

export interface ImportJobRowErrorsResponse {
  items: ImportJobRowError[];
  total: number;
  page: number;
  limit: number;
}

export interface ImportJobStageError {
  id: string;
  jobId: string;
  stageName: ImportJobStageName;
  rowNumber: number | null;
  errorType: string;
  errorMessage: string;
  errorDetails: any | null;
  createdAt: string;
  updatedAt: string;
}

export interface ImportJobStageErrorsResponse {
  items: ImportJobStageError[];
  total: number;
  page: number;
  limit: number;
}

// Import Job Statistics Types
export interface ImportJobStats {
  id: string | null;
  jobId: string;
  totalRows: number;
  exactMatches: number;
  semanticMatchesHigh: number;
  semanticMatchesMedium: number;
  semanticMatchesLow: number;
  noMatches: number;
  validationFailures: number;
  categoryMappingFailures: number;
  uomMappingFailures: number;
  skippedAlreadyImported: number;
  reprocessedChanged: number;
  previouslyRemoved: number;
  productsToUpdate: number;
  excludedProducts: number;
  totalExternalMatches: number;
  toRemoveCount: number;
  exportS3Key: string | null;
  exportFileSize: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ImportJobStatsResponse {
  stats: ImportJobStats;
}

export interface SkuDebugRequest {
  jobId: string;
  supplierSku: string;
}

export interface SkuDebugResponse {
  jobId: string;
  supplierSku: string;
  importProduct: {
    id: string;
    name: string;
    description: string | null;
    mpn: string | null;
    upc: string | null;
    brandName: string | null;
    manufacturerName: string | null;
    reopsCategoryName: string | null;
    reopsSubcategoryName: string | null;
    reopsUnitOfMeasureCode: string | null;
    hasEmbedding: boolean;
  };
  exactMatch: {
    found: boolean;
    type?: 'exact' | 'invalid';
    productId?: string;
    confidenceScore?: number;
    strategy?: string;
    matchData?: Record<string, any>;
    matchedProduct?: {
      id: string;
      name: string;
      mpn: string | null;
      upc: string | null;
      brandName: string | null;
      manufacturerName: string | null;
      unitOfMeasureCode: string | null;
    };
  };
  semanticMatch: {
    found: boolean;
    type?: 'semantic';
    productId?: string;
    confidenceScore?: number;
    strategy?: string;
    matchData?: Record<string, any>;
    matchedProduct?: {
      id: string;
      name: string;
      mpn: string | null;
      upc: string | null;
      brandName: string | null;
      manufacturerName: string | null;
      unitOfMeasureCode: string | null;
    };
    otherMatches?: Array<{
      id: string;
      score: number;
      name: string;
      mpn: string | null;
      upc: string | null;
      brandName: string | null;
      manufacturerName: string | null;
      unitOfMeasureCode: string | null;
    }>;
  };
}

export interface MatchResultItem {
  supplierSku: string;
  supplierProductName: string;
  supplierMpn?: string;
  supplierUpc?: string;
  supplierUnitOfMeasure?: string;
  supplierBrandName?: string;
  supplierManufacturerName?: string;
  confidence: number;
  potentialMatchSku: string;
  productName: string;
  matchedMpn?: string;
  matchedUpc?: string;
  matchedUnitOfMeasure?: string;
  matchedBrandName?: string;
  matchedManufacturerName?: string;
  matchData?: Record<string, any>;
  matchType: string;
  isExternalMatch?: boolean;
  externalMatchSource?: string;
  externalMatchSourceName?: string;
  matchedSuppliers?: Array<{
    supplierId: string;
    sku: string;
  }>;
}

export interface MatchResultsByTypeResponse {
  matchType:
    'exact' | 'semantic_high' | 'semantic_medium' | 'semantic_low' | 'none';
  totalCount: number;
  results: MatchResultItem[];
  page: number;
  limit: number;
}

export interface UploadSupplierCatalogRequest {
  supplierId: string;
  file: File;
  jobMode?: ImportJobMode;
  importScope?: ImportScope;
}

// Match Review Types
export type MatchFilterLevel = 'invalid' | 'exact' | 'high' | 'medium' | 'low';
export type ReviewStatus = 'pending' | 'approved' | 'rejected';
export type SortBy = 'confidence' | 'manufacturer' | 'category';
export type SortOrder = 'asc' | 'desc';

export interface JobCategory {
  id: string;
  name: string;
}

export interface JobCategoriesResponse {
  categories: JobCategory[];
}

export interface ProductSupplier {
  supplierId: string;
  supplierName: string;
  sku: string;
  supplierSearchUrl?: string | null;
}

export interface AlternativeMatch {
  matchId: string;
  productId: string;
  productName: string;
  productSku: string;
  productMpn?: string | null;
  productUpc?: string | null;
  brandName?: string | null;
  manufacturerName?: string | null;
  basePrice?: number | null;
  confidence: number | null;
  matchData?: Record<string, any> | null;
}

export interface Match {
  matchId: string;
  rowId: string;
  supplierSku: string;
  supplierProductName: string;
  supplierMpn?: string;
  supplierUpc?: string;
  supplierBasePrice?: number | null;
  supplierImageUrl?: string;
  supplierUnitOfMeasure?: string;
  supplierQtyPerUom?: number;
  supplierBrandName?: string;
  supplierManufacturerName?: string;
  confidence: number | null;
  potentialMatchSku: string;
  productId: string | null;
  productName: string;
  productSku: string;
  productMpn?: string | null;
  productUpc?: string | null;
  productImageUrl?: string | null;
  productUnitOfMeasure?: string | null;
  productQtyPerUom?: number | null;
  brandName?: string | null;
  manufacturerName?: string | null;
  basePrice?: number | null;
  matchData?: Record<string, any> | null;
  matchType: string;
  reviewStatus: ReviewStatus;
  isExternalMatch?: boolean;
  externalMatchSource?: string;
  externalMatchSourceName?: string;
  alternativeMatches?: AlternativeMatch[];
  productSuppliers?: ProductSupplier[];
}

export interface MatchesResponse {
  filters: string[];
  totalCount: number;
  results: Match[];
  page: number;
  limit: number;
}

export interface MatchActionRequest {
  reviewedBy?: string;
  reviewNotes?: string;
}

export interface MatchActionResponse {
  success: boolean;
  matchId: string;
}

export interface BatchApproveMatchesRequest extends MatchActionRequest {
  matchIds: string[];
}

export interface BatchApproveMatchesResponse {
  success: boolean;
  approvedCount: number;
}

export interface BatchRejectMatchesRequest extends MatchActionRequest {
  matchIds: string[];
}

export interface BatchRejectMatchesResponse {
  success: boolean;
  rejectedCount: number;
}

export interface AlternativeMatchesResponse {
  alternatives: AlternativeMatch[];
}

const SUPPLIER_CATALOG_BASE_URL = '/api/v1/supplier_catalog';

export class SupplierCatalogService {
  static readonly uploadSupplierCatalog = (
    data: UploadSupplierCatalogRequest,
  ): Promise<ImportJobDetailsResponse> => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('supplierId', data.supplierId);

    if (data.jobMode) {
      formData.append('jobMode', data.jobMode);
    }
    if (data.importScope) {
      formData.append('importScope', data.importScope);
    }

    return request<ImportJobDetailsResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/upload`,
      method: 'post',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  static readonly fergusonAdhocImport = (
    data: FergusonAdhocImportRequest,
  ): Promise<FergusonAdhocImportResponse> => {
    return request<FergusonAdhocImportResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/ferguson/adhoc-import`,
      method: 'post',
      data,
    });
  };

  static readonly getImportJobs = (params?: {
    page?: number;
    limit?: number;
    status?: ImportJobStatus | string;
    supplierId?: string;
  }): Promise<ImportJobListResponse> => {
    return request<ImportJobListResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs`,
      method: 'get',
      params,
    });
  };

  static readonly getImportJob = (
    jobId: string,
  ): Promise<ImportJobDetailsResponse> => {
    return request<ImportJobDetailsResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}`,
      method: 'get',
    });
  };

  static readonly getImportJobStages = (
    jobId: string,
  ): Promise<ImportJobStagesResponse> => {
    return request<ImportJobStagesResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/stages`,
      method: 'get',
    });
  };

  static readonly getImportJobErrorCounts = (
    jobId: string,
  ): Promise<ImportJobErrorCountsResponse> => {
    return request<ImportJobErrorCountsResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/errors/counts`,
      method: 'get',
    });
  };

  static readonly getRowValidationSummary = (
    jobId: string,
  ): Promise<RowValidationSummaryResponse> => {
    return request<RowValidationSummaryResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/stages/row-validation/summary`,
      method: 'get',
    });
  };

  static readonly getCategoryMappingSummary = (
    jobId: string,
    params?: { sampleLimit?: number },
  ): Promise<CategoryMappingSummaryResponse> => {
    return request<CategoryMappingSummaryResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/stages/category-mapping/summary`,
      method: 'get',
      params,
    });
  };

  static readonly getUomMappingSummary = (
    jobId: string,
    params?: { sampleLimit?: number },
  ): Promise<UomMappingSummaryResponse> => {
    return request<UomMappingSummaryResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/stages/uom-mapping/summary`,
      method: 'get',
      params,
    });
  };

  static readonly getExactMatchSummary = (
    jobId: string,
  ): Promise<ExactMatchSummaryResponse> => {
    return request<ExactMatchSummaryResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/stages/exact-match/summary`,
      method: 'get',
    });
  };

  static readonly getSemanticMatchSummary = (
    jobId: string,
  ): Promise<SemanticMatchSummaryResponse> => {
    return request<SemanticMatchSummaryResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/stages/semantic-match/summary`,
      method: 'get',
    });
  };

  static readonly getCategorizationStatistics = (
    jobId: string,
  ): Promise<CategorizationStatisticsResponse> => {
    return request<CategorizationStatisticsResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/categorization/statistics`,
      method: 'get',
    });
  };

  static readonly getCategorizationProducts = (
    jobId: string,
    params?: {
      page?: number;
      limit?: number;
      confidenceThreshold?: number;
      categorizationMethod?: string;
      sortBy?: 'confidence' | 'rowNumber' | 'name' | 'reopsCategoryName';
      sortDirection?: 'asc' | 'desc';
    },
  ): Promise<CategorizationProductsResponse> => {
    return request<CategorizationProductsResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/categorization/products`,
      method: 'get',
      params,
    });
  };

  static readonly getVectorProcessSummary = (
    jobId: string,
  ): Promise<{
    stage: {
      id: string | null;
      jobId: string;
      stageName: string;
      status: string;
      startedAt: string | null;
      completedAt: string | null;
      progressCurrent: number;
      progressTotal: number;
      errorCount: number;
      attemptsMade: number;
      maxRetries: number;
      metrics: Record<string, any> | null;
      createdAt: string;
      updatedAt: string;
    };
    summary: {
      totalToProcess: number;
      processed: number;
      total: number;
      progressPercentage: number;
      brands: {
        processed: number;
        skipped: number;
        failed: number;
      };
      manufacturers: {
        processed: number;
        skipped: number;
        failed: number;
      };
      importProducts: {
        processed: number;
        skipped: number;
        failed: number;
      };
    };
  }> => {
    return request<{
      stage: {
        id: string | null;
        jobId: string;
        stageName: string;
        status: string;
        startedAt: string | null;
        completedAt: string | null;
        progressCurrent: number;
        progressTotal: number;
        errorCount: number;
        attemptsMade: number;
        maxRetries: number;
        metrics: Record<string, any> | null;
        createdAt: string;
        updatedAt: string;
      };
      summary: {
        totalToProcess: number;
        processed: number;
        successful: number;
        total: number;
        progressPercentage: number;
        brands: {
          processed: number;
          skipped: number;
          failed: number;
        };
        manufacturers: {
          processed: number;
          skipped: number;
          failed: number;
        };
        importProducts: {
          processed: number;
          skipped: number;
          failed: number;
        };
      };
    }>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/stages/vector-process/summary`,
      method: 'get',
    });
  };

  static readonly getImportJobRowErrors = (
    jobId: string,
    params: {
      page?: number;
      limit?: number;
      severity?: ImportJobRowErrorSeverity;
      code?: string;
      fieldPath?: string;
      rowNumber?: number;
    },
  ): Promise<ImportJobRowErrorsResponse> => {
    return request<ImportJobRowErrorsResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/row-errors`,
      method: 'get',
      params,
    });
  };

  static readonly getStageErrors = (
    jobId: string,
    stageName: ImportJobStageName,
  ): Promise<ImportJobStageError[]> => {
    return request<ImportJobStageError[]>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/stage-errors/${stageName}`,
      method: 'get',
    });
  };

  static readonly cancelImportJob = (
    jobId: string,
  ): Promise<{ message: string }> => {
    return request<{ message: string }>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/cancel`,
      method: 'post',
    });
  };

  static readonly pauseImportJob = (
    jobId: string,
  ): Promise<{ message: string }> => {
    return request<{ message: string }>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/pause`,
      method: 'post',
    });
  };

  static readonly resumeImportJob = (
    jobId: string,
  ): Promise<{ message: string }> => {
    return request<{ message: string }>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/resume`,
      method: 'post',
    });
  };

  static readonly approveImportJob = (
    jobId: string,
  ): Promise<{ message: string; pendingMatchesCount: number }> => {
    return request<{ message: string; pendingMatchesCount: number }>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/approve`,
      method: 'post',
    });
  };

  static readonly getImportJobStats = (
    jobId: string,
  ): Promise<ImportJobStatsResponse> => {
    return request<ImportJobStatsResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/stats`,
      method: 'get',
    });
  };

  static readonly downloadExport = async (
    jobId: string,
    fallbackFilename = `import-results-${jobId}.zip`,
  ): Promise<void> => {
    await downloadAuthenticatedFile(
      `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/export`,
      fallbackFilename,
    );
  };

  static readonly downloadOriginalFile = async (
    jobId: string,
    fallbackFileName = 'import-upload.csv',
  ): Promise<void> => {
    await downloadAuthenticatedFile(
      `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/download_original`,
      fallbackFileName,
    );
  };

  static readonly regenerateExportFiles = (
    jobId: string,
  ): Promise<{ message: string }> => {
    return request<{ message: string }>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/regenerate-exports`,
      method: 'post',
    });
  };

  static readonly debugSkuMatching = (
    requestData: SkuDebugRequest,
  ): Promise<SkuDebugResponse> => {
    return request<SkuDebugResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/debug/sku-matching`,
      method: 'post',
      data: requestData,
    });
  };

  static readonly getMatchResultsByType = (
    jobId: string,
    matchType:
      'exact' | 'semantic_high' | 'semantic_medium' | 'semantic_low' | 'none',
    page: number = 1,
    limit: number = 50,
    searchSku?: string,
    searchManufacturer?: string,
  ): Promise<MatchResultsByTypeResponse> => {
    return request<MatchResultsByTypeResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/match-results/${matchType}`,
      method: 'get',
      params: {
        page,
        limit,
        ...(searchSku && { searchSku }),
        ...(searchManufacturer && { searchManufacturer }),
      },
    });
  };

  static readonly deleteImportJob = (
    jobId: string,
  ): Promise<{ message: string }> => {
    return request<{ message: string }>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}`,
      method: 'delete',
    });
  };

  static readonly getErrorWarningBreakdown = (
    jobId: string,
  ): Promise<{
    errors: Array<{ errorCode: string; count: number }>;
    warnings: Array<{ errorCode: string; count: number }>;
  }> => {
    return request<{
      errors: Array<{ errorCode: string; count: number }>;
      warnings: Array<{ errorCode: string; count: number }>;
    }>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/error-warning-breakdown`,
      method: 'get',
    });
  };

  static readonly getErrorDetails = (
    jobId: string,
    errorCode: string,
  ): Promise<{
    errorCode: string;
    categoryMappingDetails?: Array<{
      supplierCategory: string;
      supplierSubcategory: string;
      count: number;
    }>;
    uomMappingDetails?: Array<{
      unitOfMeasure: string;
      count: number;
    }>;
    requiredFieldDetails?: Array<{
      fieldPath: string;
      count: number;
    }>;
  }> => {
    return request<{
      errorCode: string;
      categoryMappingDetails?: Array<{
        supplierCategory: string;
        supplierSubcategory: string;
        count: number;
      }>;
      uomMappingDetails?: Array<{
        unitOfMeasure: string;
        count: number;
      }>;
      requiredFieldDetails?: Array<{
        fieldPath: string;
        count: number;
      }>;
    }>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/error-details/${errorCode}`,
      method: 'get',
    });
  };

  // Match Review Endpoints
  static readonly getJobCategories = (
    jobId: string,
  ): Promise<JobCategoriesResponse> => {
    return request<JobCategoriesResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/categories`,
      method: 'get',
    });
  };

  static readonly getMatchReviewStatusCounts = (
    jobId: string,
  ): Promise<{ pending: number; approved: number; rejected: number }> => {
    return request<{ pending: number; approved: number; rejected: number }>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/matches/review-status-counts`,
      method: 'get',
    });
  };

  static readonly getMatches = (
    jobId: string,
    params?: {
      filter?: MatchFilterLevel | MatchFilterLevel[];
      page?: number;
      limit?: number;
      searchSku?: string;
      searchManufacturer?: string;
      includeAlternatives?: boolean;
      reviewStatus?: ReviewStatus | ReviewStatus[];
      categoryId?: string;
      sortBy?: SortBy;
      sortOrder?: SortOrder;
      identifiersMatching?: boolean;
    },
  ): Promise<MatchesResponse> => {
    return request<MatchesResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/matches`,
      method: 'get',
      params,
    });
  };

  static readonly approveMatch = (
    jobId: string,
    matchId: string,
    data?: MatchActionRequest,
  ): Promise<MatchActionResponse> => {
    return request<MatchActionResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/matches/${matchId}/approve`,
      method: 'post',
      data: data || {},
    });
  };

  static readonly rejectMatch = (
    jobId: string,
    matchId: string,
    data?: MatchActionRequest,
  ): Promise<MatchActionResponse> => {
    return request<MatchActionResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/matches/${matchId}/reject`,
      method: 'post',
      data: data || {},
    });
  };

  static readonly undoMatchReview = (
    jobId: string,
    matchId: string,
  ): Promise<MatchActionResponse> => {
    return request<MatchActionResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/matches/${matchId}/undo`,
      method: 'post',
    });
  };

  static readonly batchApproveMatches = (
    jobId: string,
    data: BatchApproveMatchesRequest,
  ): Promise<BatchApproveMatchesResponse> => {
    return request<BatchApproveMatchesResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/matches/batch-approve`,
      method: 'post',
      data,
    });
  };

  static readonly batchRejectMatches = (
    jobId: string,
    data: BatchRejectMatchesRequest,
  ): Promise<BatchRejectMatchesResponse> => {
    return request<BatchRejectMatchesResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/matches/batch-reject`,
      method: 'post',
      data,
    });
  };

  static readonly getAlternativeMatches = (
    jobId: string,
    rowId: string,
    limit?: number,
  ): Promise<AlternativeMatchesResponse> => {
    return request<AlternativeMatchesResponse>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/matches/${rowId}/alternatives`,
      method: 'get',
      params: limit ? { limit } : undefined,
    });
  };

  static readonly getProductById = (
    productId: string,
  ): Promise<import('shared/types/Product').ScoredProduct> => {
    return request<import('shared/types/Product').ScoredProduct>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/products/${productId}`,
      method: 'get',
    });
  };

  static readonly getImportProductByRowId = (
    jobId: string,
    rowId: string,
  ): Promise<import('shared/types/Product').ScoredProduct> => {
    return request<import('shared/types/Product').ScoredProduct>({
      url: `${SUPPLIER_CATALOG_BASE_URL}/jobs/${jobId}/import-products/${rowId}`,
      method: 'get',
    });
  };
}
